import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { roleActions } from 'reduxStore/roles/slice';
import { selectRoles } from 'reduxStore/roles/selector';
import { selectRoleList } from 'reduxStore/types/selectors';
import { rolesConfig } from 'config/rolesConfig';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { Button, Divider, Flex, Modal, Select, Space, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

// Map to determine if a role is site-scoped or global
const ROLE_MAPPINGS = {
	[rolesConfig.GLOBAL_ADMIN]: { isSiteScoped: false },
	[rolesConfig.SITE_ADMIN]: { isSiteScoped: true },
	[rolesConfig.GLOBAL_READ_ONLY]: { isSiteScoped: false },
	[rolesConfig.SITE_READ_ONLY]: { isSiteScoped: true }
};

const RevokeRoleModal = ({ open, setOpen, personID, permissionID }) => {
	// 🎨 Hooks
	const {
		token: { paddingLG, fontSizeSM, marginXL, marginXS, paddingXL, paddingXS, paddingSM }
	} = theme.useToken();
	const dispatch = useDispatch();

	// 📦 Redux state
	const { loadingRevokeRole: loading } = useSelector((state) => state.Roles);
	const { orgID } = useSelector((state) => state.Auth.selectedProfile);
	const roleList = useSelector(selectRoleList);
	const roles = useSelector(selectRoles);
	const sites = useSelector(selectSites);
	const people = useSelector(selectPeople);

	// 🌀 Local state
	const [selectedEntityIDs, setSelectedEntityIDs] = useState([]);

	const isSiteScoped = ROLE_MAPPINGS[permissionID]?.isSiteScoped ?? false;

	// Resolve person name from store
	const person = useMemo(() => people.find((p) => p.personID === personID), [people, personID]);

	// Resolve role name from DB
	const roleName = useMemo(() => {
		const dbRole = roleList?.find((r) => r.PermissionID === permissionID);
		return dbRole?.Name || 'Role';
	}, [roleList, permissionID]);

	// ✅ Build site options directly from roles store — no index dependency
	const siteOptions = useMemo(() => {
		if (!isSiteScoped || !personID || !permissionID) return [];
		return roles
			.filter((r) => r.personID === personID && r.permissionID === permissionID)
			.map((r) => {
				const site = sites.find((s) => s.siteID === r.entityID);
				return {
					label: site?.name || `Site ${r.entityID}`,
					value: r.entityID
				};
			});
	}, [roles, sites, personID, permissionID, isSiteScoped]);

	// Reset selection every time the modal opens
	useEffect(() => {
		if (open) setSelectedEntityIDs([]);
	}, [open]);

	const handleCancel = () => {
		setOpen(false);
		setSelectedEntityIDs([]);
	};

	const handleDelete = () => {
		if (!personID || !permissionID) return;
		if (isSiteScoped && selectedEntityIDs.length === 0) return;

		const entityIDs = isSiteScoped ? selectedEntityIDs : [orgID];

		entityIDs.forEach((entityID) => {
			dispatch(
				roleActions.revokeRole({
					personID,
					permissionID,
					entityID
				})
			);
		});

		handleCancel();
	};

	const deleteTitle = `Revoke ${roleName}?`;
	const disableDelete = isSiteScoped && selectedEntityIDs.length === 0;

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			closeIcon={false}
			centered
			destroyOnClose
			footer={[
				<Flex
					align='center'
					justify='center'
					style={{ marginTop: marginXL, padding: `0px ${paddingXS}px` }}
					gap={12}
					key='footer'
				>
					<Button onClick={handleCancel} type='default' disabled={loading} block>
						Cancel
					</Button>
					<Button
						onClick={handleDelete}
						loading={loading}
						disabled={loading || disableDelete}
						type='primary'
						danger
						block
					>
						Revoke
					</Button>
				</Flex>
			]}
			width={380}
			styles={{ content: { padding: paddingXL } }}
		>
			<Flex vertical align='center' justify='center' gap={paddingSM}>
				<DeletedFilled />
				<Flex vertical gap={paddingLG} style={{ width: '100%' }}>
					<Space
						size={paddingXS}
						direction='vertical'
						align='center'
						style={{ margin: `${paddingXS}px 0px`, width: '100%' }}
					>
						<Title level={5} style={{ margin: 0, lineHeight: '24px', textAlign: 'center' }}>
							{deleteTitle}
						</Title>
						<Text
							style={{ fontSize: fontSizeSM, lineHeight: '24px', textAlign: 'center', display: 'block' }}
						>
							This action is permanent and cannot be undone.
						</Text>
					</Space>

					<Flex vertical gap={marginXS}>
						<Divider orientation='center' plain style={{ margin: 0, padding: 0 }}>
							<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Person</Text>
						</Divider>
						<Text style={{ fontSize: fontSizeSM, fontWeight: 500, textAlign: 'center' }}>
							{person?.displayName || person?.email || '-'}
						</Text>

						{/* Show site selector ONLY for site-scoped roles */}
						{isSiteScoped && (
							<>
								<Divider orientation='center' plain style={{ margin: '12px 0 0 0', padding: 0 }}>
									<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>
										Select sites to revoke
									</Text>
								</Divider>
								<Select
									mode='multiple'
									placeholder='Select sites to revoke'
									options={siteOptions}
									value={selectedEntityIDs}
									onChange={(values) => setSelectedEntityIDs(values)}
									style={{ width: '100%', marginTop: 8 }}
									maxTagCount='responsive'
								/>
							</>
						)}
					</Flex>
				</Flex>
			</Flex>
		</Modal>
	);
};

export default RevokeRoleModal;
